import React from 'react';
import * as yup from 'yup';
import { shared } from '../config';
import { getDimensions, hasDimensions } from './images';
import { RichText } from './RichText';
import { schema as videoModalSchema } from './builders/video_modal';
import { schema as modalSchema } from './builders/cms_modal';

const imageFromCMSSchema = nullable => {
  return yup
    .object({
      src: nullable ? yup.string().nullable() : yup.string().required(),
      alt: nullable ? yup.string().nullable() : yup.string().required(),
      width: yup.number().nullable(),
      height: yup.number().nullable(),
    })
    .transform(value => {
      if (!value?.filename) {
        return null;
      }

      const dimensions = hasDimensions(value.filename)
        ? getDimensions(value.filename)
        : { width: value.width, height: value.height };
      return {
        src: value.filename,
        alt: value.alt || value.name,
        ...dimensions,
        component: 'image',
      };
    });
};

export const nullableImageFromCMSSchema = imageFromCMSSchema(true).nullable();
export const requiredImageFromCMSSchema = imageFromCMSSchema(false).required();

export const responsiveImageFromCMSSchema = yup
  .object({
    src: yup.string().nullable(),
    alt: yup.string().nullable(),
    width: yup.number().nullable(),
    height: yup.number().nullable(),
  })
  .nullable()
  .transform(value => {
    const responsiveImage = value?.images?.[0]?.asset;
    if (responsiveImage?.filename) {
      return {
        src: responsiveImage.filename,
        alt: responsiveImage.alt || responsiveImage.name,
        ...getDimensions(responsiveImage.filename),
      };
    }
    return null;
  });

export const richTextFromCMSSchema = yup
  .object()
  .transform(value => <RichText content={value} />);

export const inlineRichTextFromCMSSchema = yup
  .object()
  .transform(value => <RichText content={value} inline />);

export const createInlineRichTextFromCMSSchema = level =>
  yup
    .object()
    .transform(value => <RichText content={value} inline level={level} />);

export const linkSchema = yup
  .object({
    target: yup
      .string()
      .required()
      .transform(target => {
        if (target.startsWith('http') || target.startsWith('mailto:')) {
          return target;
        }

        if (!target.startsWith('/')) {
          target = '/' + target;
        }

        const url = new URL(target, `https://www.${shared.SITE_NAME}`); // now target is a valid relative path
        if (!url.pathname.endsWith('/')) {
          url.pathname += '/';
        }

        return url.pathname + url.search + url.hash;
      }),
    text: yup.string().required(),
    variant: yup.string().nullable(),
    headerText: yup.string().nullable(),
    triggerModal: yup.boolean(),
    modal: yup.lazy(item => {
      switch (item?.component) {
        case 'video_modal':
          return videoModalSchema;
        default:
          return modalSchema;
      }
    }),
    openNewTab: yup.boolean(),
    icon: nullableImageFromCMSSchema,
    iconAlignment: yup.string().nullable(),
  })
  // Yup will default a missing object to an empty object with default props
  .default(undefined)
  .transform(
    link =>
      link && {
        anchor: link.link_target?.anchor,
        text: link.link_text,
        target:
          (link.link_target?.cached_url || link.link_target?.url) +
          (link.link_target?.anchor ? `#${link.link_target.anchor}` : ''),
        variant: link.variant || undefined,
        component: 'link',
        headerText: link.header_text || undefined,
        triggerModal: link.trigger_modal,
        modal: link.modal?.[0] || null,
        openNewTab: link.link_target?.target === '_blank' || false,
        icon: link.icon || undefined,
        iconAlignment: link.icon_alignment || undefined,
      },
  );

export const headlineVariantsSchema = level => {
  const headingSchema = level
    ? createInlineRichTextFromCMSSchema(level)
    : inlineRichTextFromCMSSchema;
  return yup
    .array(
      yup.object({
        variant: yup.string().required(),
        heading: headingSchema,
      }),
    )
    .nullable();
};
